import { useNavigate } from 'react-router-dom'
import { Clock, Heart, UserX } from 'lucide-react'
import { useFeedReview, type FeedReviewEntry } from '@/shared/hooks/useFeed'
import { resolveProfileMediaUrl } from '@/shared/lib/resolveProfileMediaUrl'

type Kind = 'skipped' | 'liked'

const titles: Record<Kind, { title: string; subtitle: string }> = {
  skipped: {
    title: 'Отсеянные',
    subtitle: 'Ты свайпнул влево — эти анкеты не попадут к тебе в ленту снова.',
  },
  liked: {
    title: 'Симпатии',
    subtitle: 'Ты свайпнул вправо. Если будет взаимность — появится мэтч.',
  },
}

function PeerStatus({ row }: { row: FeedReviewEntry }) {
  const r = row.theirReaction ?? 'none'
  if (r === 'skip') {
    return (
      <div
        className="flex shrink-0 flex-col items-center gap-0.5 text-gray-500 dark:text-gray-400"
        title="Пропустил тебя в своей ленте"
      >
        <UserX className="h-6 w-6" strokeWidth={2} aria-hidden />
        <span className="max-w-[4.5rem] text-center text-[10px] font-medium leading-tight">Пропуск</span>
      </div>
    )
  }
  if (r === 'like') {
    return (
      <div
        className="flex shrink-0 flex-col items-center gap-0.5 text-rose-500 dark:text-rose-400"
        title="Лайкнул тебя (мэтч возможен, если ты тоже лайкнешь)"
      >
        <Heart className="h-6 w-6 fill-current" strokeWidth={2} aria-hidden />
        <span className="max-w-[4.5rem] text-center text-[10px] font-medium leading-tight">К тебе</span>
      </div>
    )
  }
  return (
    <div
      className="flex shrink-0 flex-col items-center gap-0.5 text-amber-600 dark:text-amber-400"
      title="Пока не оценил тебя: твоя анкета у него в ленте ещё не дошла или он не свайпнул"
    >
      <Clock className="h-6 w-6" strokeWidth={2} aria-hidden />
      <span className="max-w-[4.5rem] text-center text-[10px] font-medium leading-tight">Ожидание</span>
    </div>
  )
}

export function FeedReview({ kind }: { kind: Kind }) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useFeedReview(kind)
  const copy = titles[kind]

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 p-4 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-full px-2 py-1 text-xl text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
            aria-label="На главный"
          >
            ←
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{copy.title}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              Справа: ожидание их реакции · пропуск тебя · симпатия к тебе
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex animate-pulse gap-3 rounded-xl border border-gray-100 p-3 dark:border-gray-800">
                <div className="h-16 w-16 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
                </div>
              </div>
            ))}
          </div>
        )}

        {isError && (
          <p className="text-center text-red-600 dark:text-red-400">Не удалось загрузить список. Попробуй ещё раз.</p>
        )}

        {!isLoading && !isError && (!data || data.length === 0) && (
          <p className="py-12 text-center text-gray-600 dark:text-gray-400">Записей пока нет.</p>
        )}

        {!isLoading && data && data.length > 0 && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {data.map((row) => (
              <li key={`${row.userId}-${row.at}`} className="flex items-start gap-3 py-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  {row.thumbUrl ? (
                    <img
                      src={resolveProfileMediaUrl(row.thumbUrl)}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-2xl text-gray-400">?</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {row.name}
                    {row.age != null ? `, ${row.age}` : ''}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{row.city || 'Город не указан'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {new Date(row.at).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
                <PeerStatus row={row} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
